import { useMemo, useState } from "react";
import { Search, X, Plus } from "lucide-react";
import { Input } from "../components/ui/input";

/**
 * Step 2 of the assessment.
 *
 * Shows skill chips from the chosen field(s) by default (technical + soft
 * tagged), and a separate "add skills from other areas" search that surfaces
 * chips from the OTHER 9 field categories only (no orphan skills sneak in).
 */
export const FieldFilteredSkillsStep = ({
  fields,                 // raw output of /api/fields
  primaryFieldId,
  secondaryFieldId,
  selectedTechnical,
  selectedSoft,
  onToggleTechnical,
  onToggleSoft,
}) => {
  const [extraQuery, setExtraQuery] = useState("");

  const { primaryField, inFieldTech, inFieldSoft, outOfFieldTech, outOfFieldSoft } =
    useMemo(() => {
      const inIds = new Set([primaryFieldId, secondaryFieldId].filter(Boolean));
      const inFields = fields.filter((f) => inIds.has(f.id));
      const outFields = fields.filter(
        (f) => !inIds.has(f.id) && f.id !== "_other"
      );

      const uniqByName = (arr) => {
        const out = new Map();
        for (const item of arr) {
          if (!out.has(item.name)) out.set(item.name, item);
        }
        return Array.from(out.values());
      };

      return {
        primaryField: fields.find((f) => f.id === primaryFieldId) || null,
        inFieldTech: uniqByName(inFields.flatMap((f) => f.technical_skills)),
        inFieldSoft: uniqByName(inFields.flatMap((f) => f.soft_skills)),
        outOfFieldTech: uniqByName(outFields.flatMap((f) => f.technical_skills)),
        outOfFieldSoft: uniqByName(outFields.flatMap((f) => f.soft_skills)),
      };
    }, [fields, primaryFieldId, secondaryFieldId]);

  const techSet = new Set(selectedTechnical);
  const softSet = new Set(selectedSoft);

  const renderChips = (items, isSoft, testPrefix) => {
    if (!items.length) return null;
    return items.map((s) => {
      const active = (isSoft ? softSet : techSet).has(s.name);
      return (
        <button
          type="button"
          key={`${isSoft ? "soft" : "tech"}-${s.name}`}
          data-testid={`${testPrefix}-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => (isSoft ? onToggleSoft(s.name) : onToggleTechnical(s.name))}
          className={`text-sm px-3 py-2 rounded-md border transition-colors ${
            active
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-700 border-slate-300 hover:border-slate-900 hover:text-slate-900"
          }`}
        >
          {s.name}
        </button>
      );
    });
  };

  // Out-of-field search.
  const q = extraQuery.trim().toLowerCase();
  const filteredOutOfFieldTech = q
    ? outOfFieldTech.filter((s) => s.name.toLowerCase().includes(q))
    : [];
  const filteredOutOfFieldSoft = q
    ? outOfFieldSoft.filter((s) => s.name.toLowerCase().includes(q))
    : [];

  const totalSelected = selectedTechnical.length + selectedSoft.length;

  return (
    <div data-testid="skills-step" className="space-y-7">
      <div>
        <h2
          data-testid="skills-step-title"
          className="font-display text-2xl sm:text-3xl font-bold text-slate-900"
        >
          What skills do you actually have?
        </h2>
        <p className="mt-3 text-sm text-slate-600 max-w-2xl">
          We&rsquo;ve narrowed the list to the skills that matter for{" "}
          {primaryField ? <b>{primaryField.label}</b> : "your field"} roles.
          Tap every skill you have hands-on experience with — don&rsquo;t overclaim,
          recruiters can tell.
        </p>
      </div>

      {/* Selected summary */}
      {totalSelected > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
            Selected ({totalSelected})
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedTechnical.map((name) => (
              <button
                key={`sel-t-${name}`}
                onClick={() => onToggleTechnical(name)}
                className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-xs font-medium px-3 py-1.5 hover:bg-slate-800"
              >
                {name} <X size={12} />
              </button>
            ))}
            {selectedSoft.map((name) => (
              <button
                key={`sel-s-${name}`}
                onClick={() => onToggleSoft(name)}
                className="inline-flex items-center gap-1 rounded-full bg-amber-600 text-white text-xs font-medium px-3 py-1.5 hover:bg-amber-700"
              >
                {name} <X size={12} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* In-field chips */}
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
          Technical skills ({inFieldTech.length})
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Higher-priority skills appear first.
        </p>
        <div data-testid="in-field-tech-chips" className="mt-3 flex flex-wrap gap-2">
          {renderChips(inFieldTech, false, "tech-chip")}
        </div>
      </div>

      {inFieldSoft.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
            Soft skills ({inFieldSoft.length})
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Pick the soft skills that genuinely describe how you work.
          </p>
          <div data-testid="in-field-soft-chips" className="mt-3 flex flex-wrap gap-2">
            {renderChips(inFieldSoft, true, "soft-chip")}
          </div>
        </div>
      )}

      {/* Escape-hatch search across other fields */}
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Plus size={14} />
          Add skills from other areas
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Got skills outside this field? Search the other 9 field categories.
        </p>
        <div className="relative mt-3">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            data-testid="extra-search"
            placeholder="e.g. AWS, Python, Stakeholder Engagement..."
            value={extraQuery}
            onChange={(e) => setExtraQuery(e.target.value)}
            className="pl-9 h-10 bg-white"
          />
        </div>
        {extraQuery && (
          <div className="mt-3">
            {filteredOutOfFieldTech.length + filteredOutOfFieldSoft.length === 0 ? (
              <p className="text-xs text-slate-500">No matches in other fields.</p>
            ) : (
              <div data-testid="extra-chips" className="flex flex-wrap gap-2">
                {renderChips(filteredOutOfFieldTech, false, "extra-tech-chip")}
                {renderChips(filteredOutOfFieldSoft, true, "extra-soft-chip")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldFilteredSkillsStep;
